import { http } from './http';
import {
  CandidateBilan,
  CandidateDocumentRequest,
  CandidateDocuments,
  CandidateExam,
  DocumentRequestStatus,
  CandidateLesson,
  CandidateLivret,
  CandidatePayment,
  CandidateProfile,
  CandidateProgress,
  CandidateSignatureDocument,
  CandidateSlot,
  CandidateWithdrawal,
  DeclaredWithdrawal,
  LearningBooklet,
  PayableInvoicesResponse,
  SessionResponse,
  SignatureChallenge,
  StartingEvaluation,
  VerifyResponse,
} from '../types';
import { licenceFollowupOf, type LicenceFollowup } from '../lib/permis';
import { pendingRatingsOf, type PendingRatings } from '../lib/instructorRating';
import { examDateRequestOf, type ExamDateRequestView } from '../lib/examDateRequest';

const BASE = '/v1/candidate-portal';

/** Demande l'envoi d'un lien magique de connexion. */
export async function requestMagicLink(email: string): Promise<void> {
  await http.post(`${BASE}/auth/request-link`, { email });
}

/** Échange un jeton de lien magique contre une session candidat. */
export async function loginWithPassword(
  email: string,
  password: string,
): Promise<SessionResponse> {
  const { data } = await http.post<SessionResponse>(`${BASE}/auth/login`, {
    email,
    password,
  });
  return data;
}

export async function setPassword(
  newPassword: string,
  currentPassword?: string,
): Promise<{ ok: boolean; hasPassword: boolean }> {
  const { data } = await http.post<{ ok: boolean; hasPassword: boolean }>(
    `${BASE}/me/password`,
    { newPassword, currentPassword },
  );
  return data;
}

export async function verifyMagicLink(token: string): Promise<VerifyResponse> {
  const { data } = await http.post<VerifyResponse>(`${BASE}/auth/verify`, {
    token,
  });
  return data;
}

/** Échange un jeton de rafraîchissement contre une nouvelle session. */
export async function refreshSession(
  refreshToken: string,
): Promise<SessionResponse> {
  const { data } = await http.post<SessionResponse>(`${BASE}/auth/refresh`, {
    refreshToken,
  });
  return data;
}

export async function fetchProfile(): Promise<CandidateProfile> {
  const { data } = await http.get<CandidateProfile>(`${BASE}/me`);
  return data;
}

export async function fetchSlots(): Promise<CandidateSlot[]> {
  const { data } = await http.get<CandidateSlot[]>(`${BASE}/me/slots`);
  return data;
}

export async function fetchLessons(): Promise<CandidateLesson[]> {
  const { data } = await http.get<CandidateLesson[]>(`${BASE}/me/lessons`);
  return data;
}

export async function bookSlot(slotId: string): Promise<CandidateLesson> {
  const { data } = await http.post<CandidateLesson>(
    `${BASE}/me/slots/${slotId}/book`,
  );
  return data;
}

export async function cancelLesson(lessonId: string): Promise<void> {
  await http.post(`${BASE}/me/lessons/${lessonId}/cancel`);
}

export async function fetchPayments(): Promise<CandidatePayment[]> {
  const { data } = await http.get<CandidatePayment[]>(`${BASE}/me/payments`);
  return data;
}

export async function fetchPayableInvoices(): Promise<PayableInvoicesResponse> {
  const { data } = await http.get<PayableInvoicesResponse>(
    `${BASE}/me/billing/invoices`,
  );
  return data;
}

export async function createInvoiceCheckout(
  invoiceId: string,
): Promise<{ url: string }> {
  const { data } = await http.post<{ url: string }>(
    `${BASE}/me/billing/invoices/${invoiceId}/checkout`,
  );
  return data;
}

/** Re-télécharge le PDF d'une facture et déclenche l'enregistrement. */
export async function downloadInvoicePdf(
  invoiceId: string,
  invoiceNumber: string,
): Promise<void> {
  const { data } = await http.get<Blob>(
    `${BASE}/me/billing/invoices/${invoiceId}/pdf`,
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `facture_${invoiceNumber}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export interface ConfirmCheckoutResult {
  paid: boolean;
  status: string;
  invoiceId?: string;
  invoiceNumber?: string;
}

/**
 * Confirme un paiement au retour de Stripe (page de succès) : le back vérifie
 * la session Checkout et officialise la facture si elle est réglée, sans
 * attendre le webhook.
 */
export async function confirmCheckout(
  sessionId: string,
): Promise<ConfirmCheckoutResult> {
  const { data } = await http.post<ConfirmCheckoutResult>(
    `${BASE}/me/billing/checkout/${sessionId}/confirm`,
  );
  return data;
}

export interface CatalogItemDto {
  id: string;
  name: string;
  serviceType: string;
  licenseType: string;
  priceInclTax: number;
  vatRate: number;
  hours: number;
  /** Description libre de l'auto-école (absente si non renseignée). */
  description?: string | null;
}
export interface CatalogFormulaDto {
  id: string;
  name: string;
  priceInclTax: number;
  vatRate: number;
  itemsCount: number;
}
export interface SchoolCatalog {
  /** Faux tant que l'auto-école n'a pas activé le paiement en ligne : rien ne peut être commandé. */
  paymentEnabled: boolean;
  /** Commandes de l'auto-école encore à facturer : elles se règlent sur leur facture, pas ici. */
  /** Commandes de l'auto-école à facturer et leurs prestations, qui ne se rachètent pas ici (F47). */
  pendingOrders?: Array<{ reference: string; title: string; catalogItemIds?: string[]; catalogFormulaIds?: string[] }>;
  /**
   * Boutique fermée tant que l'évaluation de départ n'est pas faite, si l'auto-école l'a choisi (J8) : rien n'est
   * proposé, `message` dit pourquoi. Absent : ouverte.
   */
  access?: { open: true } | { open: false; reason: 'evaluation-pending' | 'evaluation-not-sent'; message: string };
  items: CatalogItemDto[];
  formulas: CatalogFormulaDto[];
}

export async function fetchCatalog(): Promise<SchoolCatalog> {
  const { data } = await http.get<SchoolCatalog>(`${BASE}/me/catalog`);
  return data;
}

export interface CreateOrderPayload {
  items?: { catalogItemId: string; quantity: number }[];
  formulas?: { catalogFormulaId: string; quantity: number }[];
}

/** Une commande se règle en ligne tout de suite : la facture n'est émise qu'une fois payée. */
export interface CreateOrderResult {
  requiresPayment: boolean;
  checkoutUrl?: string;
  totalTtc: number;
}

export async function createOrder(
  payload: CreateOrderPayload,
): Promise<CreateOrderResult> {
  const { data } = await http.post<CreateOrderResult>(
    `${BASE}/me/orders`,
    payload,
  );
  return data;
}

/** Récapitulatif de convocation à l'examen envoyé par l'auto-école. */
export interface CandidateConvocation {
  id: string;
  fileName: string;
  examStartsAt: string;
  center: string;
  sentAt: string;
}

export async function fetchConvocations(): Promise<CandidateConvocation[]> {
  const { data } = await http.get<CandidateConvocation[]>(
    `${BASE}/me/convocations`,
  );
  return Array.isArray(data) ? data : [];
}

/** Télécharge le récapitulatif de convocation et déclenche l'enregistrement. */
export async function downloadConvocationPdf(
  convocation: Pick<CandidateConvocation, 'id' | 'fileName'>,
): Promise<void> {
  const { data } = await http.get<Blob>(
    `${BASE}/me/convocations/${convocation.id}/pdf`,
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = convocation.fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export async function fetchDocuments(): Promise<CandidateDocuments> {
  const { data } = await http.get<CandidateDocuments>(`${BASE}/me/documents`);
  return data;
}

/** Devis et commandes que l'auto-école a envoyés en signature. */
export async function fetchSignatures(): Promise<
  CandidateSignatureDocument[]
> {
  const { data } = await http.get<CandidateSignatureDocument[]>(
    `${BASE}/me/signatures`,
  );
  return Array.isArray(data) ? data : [];
}

/** Demande le code de signature d'un document, envoyé par e-mail à l'élève. */
export async function openSignature(
  requestId: string,
): Promise<SignatureChallenge> {
  const { data } = await http.post<SignatureChallenge>(
    `${BASE}/me/signatures/${requestId}/access`,
  );
  return data;
}

/**
 * Ventes conclues à distance de l'élève (« Renoncer au contrat ici », T17) : ouvertes, rétractées ou closes.
 * Une réponse qui n'est pas une liste vaut liste vide.
 */
export async function fetchWithdrawals(): Promise<CandidateWithdrawal[]> {
  const { data } = await http.get<CandidateWithdrawal[]>(
    `${BASE}/me/withdrawals`,
  );
  return Array.isArray(data) ? data : [];
}

/**
 * Rétractation confirmée depuis l'espace élève : rend la vente avec l'accusé enregistré par le serveur.
 * 404 : document d'un autre élève ; 409 : délai passé ; une seconde confirmation rend la même rétractation.
 */
export async function confirmWithdrawal(
  id: string,
  declared: DeclaredWithdrawal,
): Promise<CandidateWithdrawal> {
  const { data } = await http.post<CandidateWithdrawal>(
    `${BASE}/me/withdrawals/${id}`,
    { name: declared.name, contract: declared.contract, email: declared.email },
  );
  return data;
}

export async function fetchLivret(): Promise<CandidateLivret> {
  const { data } = await http.get<CandidateLivret>(`${BASE}/me/livret`);
  return data;
}

export async function fetchLearningBooklet(): Promise<LearningBooklet> {
  const { data } = await http.get<LearningBooklet>(
    `${BASE}/me/learning-booklet`,
  );
  return data;
}

export async function setLearningSelfAssessment(
  itemCode: string,
  selfAssessment: number | null,
): Promise<LearningBooklet> {
  const { data } = await http.patch<LearningBooklet>(
    `${BASE}/me/learning-booklet/${itemCode}`,
    { selfAssessment },
  );
  return data;
}

export async function fetchStartingEvaluation(): Promise<StartingEvaluation> {
  const { data } = await http.get<StartingEvaluation>(
    `${BASE}/me/starting-evaluation`,
  );
  return data;
}

export async function submitStartingEvaluation(
  answers: Record<string, string | string[]>,
): Promise<StartingEvaluation> {
  const { data } = await http.post<StartingEvaluation>(
    `${BASE}/me/starting-evaluation`,
    { answers },
  );
  return data;
}

export async function fetchProgress(): Promise<CandidateProgress> {
  const { data } = await http.get<CandidateProgress>(`${BASE}/me/progress`);
  return data;
}

export async function fetchExam(): Promise<CandidateExam | null> {
  const { data } = await http.get<CandidateExam | null>(`${BASE}/me/exam`);
  return data;
}

/** Demande de date d'examen de l'élève (en attente ou réponse de l'auto-école) ; `null` si illisible. */
export async function fetchExamDateRequest(): Promise<ExamDateRequestView | null> {
  const { data } = await http.get(`${BASE}/me/exam-date-request`);
  return examDateRequestOf(data);
}

export async function submitExamDateRequest(body: {
  earliestDate?: string;
  latestDate?: string;
  note?: string;
}): Promise<ExamDateRequestView | null> {
  const { data } = await http.post(`${BASE}/me/exam-date-request`, body);
  return examDateRequestOf(data);
}

export async function cancelExamDateRequest(): Promise<ExamDateRequestView | null> {
  const { data } = await http.post(`${BASE}/me/exam-date-request/cancel`);
  return examDateRequestOf(data);
}

export async function fetchHistory(): Promise<CandidateBilan[]> {
  const { data } = await http.get<CandidateBilan[]>(`${BASE}/me/history`);
  return data;
}

export async function fetchDocumentRequests(): Promise<
  CandidateDocumentRequest[]
> {
  const { data } = await http.get<CandidateDocumentRequest[]>(
    `${BASE}/me/document-requests`,
  );
  return data;
}

export async function markDocumentProvided(
  id: string,
): Promise<{ id: string; status: DocumentRequestStatus }> {
  const { data } = await http.post<{ id: string; status: DocumentRequestStatus }>(
    `${BASE}/me/document-requests/${id}/provided`,
  );
  return data;
}

export async function uploadDocument(
  id: string,
  file: File,
): Promise<{ id: string; status: DocumentRequestStatus; fileName: string }> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await http.post<{
    id: string;
    status: DocumentRequestStatus;
    fileName: string;
  }>(`${BASE}/me/document-requests/${id}/upload`, form, {
    // Laisse axios calculer le boundary multipart (sinon l'en-tête JSON par
    // défaut de l'instance casse l'envoi).
    headers: { 'Content-Type': undefined as unknown as string },
  });
  return data;
}

// ── Messagerie avec l'auto-école ────────────────────────────────────────────

const MESSAGING = `${BASE}/me/messaging`;

/** Pas de temps réel : la liste et le fil ouvert sont relus à cette cadence. */
export const MESSAGING_REFRESH_MS = 20_000;

/** OFFICE : boîte commune du secrétariat ; DIRECT : un moniteur ; GROUP : groupe créé par l'école. */
export type MessagingConversationKind = 'OFFICE' | 'DIRECT' | 'GROUP';

/** Présence vue par l'élève : seulement « en ligne », jamais l'heure du dernier passage. */
export interface MessagingPresence {
  online: boolean;
}

export interface MessagingContacts {
  office: boolean;
  /** Le secrétariat est en ligne dès qu'un de ses membres l'est. */
  officePresence?: MessagingPresence | null;
  instructors: { id: string; name: string; presence?: MessagingPresence | null }[];
}

export interface MessagingConversation {
  id: string;
  kind: MessagingConversationKind;
  title: string;
  /** Présence du moniteur ou du secrétariat ; `null` pour un groupe. */
  presence?: MessagingPresence | null;
  unread: number;
  updatedAt: string;
  last: { body: string; author: string; at: string } | null;
}

export interface MessagingMember {
  kind: 'staff' | 'student';
  role: string;
  name: string;
  presence?: MessagingPresence | null;
}

export interface MessagingMessage {
  id: string;
  sequence: number;
  mine: boolean;
  /** `assistant` : réponse générée par Permio AI (une IA), au nom de l'auto-école. */
  authorKind: 'staff' | 'student' | 'assistant';
  author: string;
  body: string;
  createdAt: string;
}

export interface MessagingThread {
  conversation: {
    id: string;
    kind: MessagingConversationKind;
    title: string;
    canWrite: boolean;
    presence?: MessagingPresence | null;
  };
  members: MessagingMember[];
  /** Du plus ancien au plus récent, 50 au plus. */
  items: MessagingMessage[];
  hasMore: boolean;
}

/** Le secrétariat et les moniteurs à qui l'élève peut écrire. */
export async function fetchMessagingContacts(): Promise<MessagingContacts> {
  const { data } = await http.get<MessagingContacts>(`${MESSAGING}/contacts`);
  return data;
}

/** Conversations de l'élève, la plus récente d'abord. */
export async function fetchConversations(): Promise<MessagingConversation[]> {
  const { data } = await http.get<MessagingConversation[]>(
    `${MESSAGING}/conversations`,
  );
  return Array.isArray(data) ? data : [];
}

/** Ouvre, ou retrouve, l'échange avec le secrétariat. */
export async function openOfficeConversation(): Promise<{ id: string }> {
  const { data } = await http.post<{ id: string }>(
    `${MESSAGING}/conversations/office`,
  );
  return data;
}

/** Ouvre, ou retrouve, l'échange à deux avec un moniteur. */
export async function openDirectConversation(
  instructorId: string,
): Promise<{ id: string }> {
  const { data } = await http.post<{ id: string }>(
    `${MESSAGING}/conversations/direct`,
    { instructorId },
  );
  return data;
}

/** Messages d'une conversation ; `before` charge ceux qui précèdent cette séquence. */
export async function fetchConversationMessages(
  conversationId: string,
  before?: number,
): Promise<MessagingThread> {
  const { data } = await http.get<MessagingThread>(
    `${MESSAGING}/conversations/${conversationId}/messages`,
    { params: before === undefined ? undefined : { before } },
  );
  return data;
}

/** `requestId` : UUID choisi par le client, le même en cas de nouvel essai. */
export async function sendConversationMessage(
  conversationId: string,
  payload: { requestId: string; body: string },
): Promise<{ id: string; sequence: number }> {
  const { data } = await http.post<{ id: string; sequence: number }>(
    `${MESSAGING}/conversations/${conversationId}/messages`,
    payload,
  );
  return data;
}

/** Marque la conversation lue jusqu'à `sequence`. */
export async function markConversationRead(
  conversationId: string,
  sequence: number,
): Promise<{ sequence: number }> {
  const { data } = await http.post<{ sequence: number }>(
    `${MESSAGING}/conversations/${conversationId}/read`,
    { sequence },
  );
  return data;
}

/**
 * Signale un problème au service technique Permio, par e-mail. L'élève, son école et son
 * navigateur sont ajoutés par le serveur ; `page` est le chemin de la page d'où part le signalement.
 */
export async function reportProblem(payload: {
  message: string;
  page?: string;
}): Promise<{ sent: boolean; replyTo: string | null }> {
  const { data } = await http.post<{ sent: boolean; replyTo: string | null }>(
    `${BASE}/me/support/reports`,
    payload,
  );
  return data;
}

/** Signal de l'élève connecté : l'espace est ouvert et utilisé. */
export async function sendPresence(): Promise<void> {
  await http.post('/v1/candidate-portal/me/presence');
}

// ── Photos de profil ────────────────────────────────────────────────────────

/** Sa photo et celles des moniteurs de l'école, par compte (messagerie). Adresses versionnées. */
export interface CandidatePhotos {
  me: string | null;
  staff: Record<string, string>;
}

const photoAddress = (value: unknown): value is string =>
  typeof value === 'string' && value.startsWith('/v1/candidate-portal/');

/** Une réponse inattendue vaut « aucune photo » : les initiales restent. */
export async function fetchPhotos(): Promise<CandidatePhotos> {
  const { data } = await http.get<unknown>(`${BASE}/me/photos`);
  const raw = (data && typeof data === 'object' && !Array.isArray(data) ? data : {}) as {
    me?: unknown;
    staff?: unknown;
  };
  const staff =
    raw.staff && typeof raw.staff === 'object' && !Array.isArray(raw.staff)
      ? Object.fromEntries(
          Object.entries(raw.staff).filter((entry): entry is [string, string] =>
            photoAddress(entry[1]),
          ),
        )
      : {};
  return { me: photoAddress(raw.me) ? raw.me : null, staff };
}

/** Image d'une photo (route protégée : le jeton part en en-tête). */
export async function fetchPhotoImage(url: string): Promise<Blob> {
  const { data } = await http.get<Blob>(url, { responseType: 'blob' });
  return data;
}

/** L'élève pose ou remplace sa photo. */
export async function uploadMyPhoto(photo: Blob): Promise<{ photoUrl: string | null }> {
  const form = new FormData();
  form.append('file', photo, 'photo.jpg');
  const { data } = await http.post<{ photoUrl: string | null }>(`${BASE}/me/photo`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** L'élève retire sa photo : ses initiales reviennent. */
export async function removeMyPhoto(): Promise<{ photoUrl: string | null }> {
  const { data } = await http.delete<{ photoUrl: string | null }>(`${BASE}/me/photo`);
  return data;
}


/** Mon permis : parcours lancé par l'auto-école à l'obtention du permis (`null` sans parcours). */
export async function fetchLicenceFollowup(): Promise<LicenceFollowup | null> {
  const { data } = await http.get<unknown>(`${BASE}/me/licence-followup`);
  return licenceFollowupOf(data);
}

/** « Êtes-vous hébergé(e) chez un tiers ? » ; « oui » fait demander les pièces de l'hébergeur. */
export async function answerLicenceHosting(hosted: boolean): Promise<LicenceFollowup | null> {
  const { data } = await http.post<unknown>(`${BASE}/me/licence-followup/hosting`, { hosted });
  return licenceFollowupOf(data);
}

/** Questionnaire de satisfaction (une seule réponse). */
export async function submitLicenceSurvey(
  answers: Record<string, number | boolean | string>,
): Promise<LicenceFollowup | null> {
  const { data } = await http.post<unknown>(`${BASE}/me/licence-followup/survey`, { answers });
  return licenceFollowupOf(data);
}

/** Leçons émargées des 14 derniers jours que l'élève peut encore noter, et critères proposés. */
export async function fetchPendingRatings(): Promise<PendingRatings> {
  const { data } = await http.get<unknown>(`${BASE}/me/instructor-ratings/pending`);
  return pendingRatingsOf(data);
}

/** Note du moniteur sur une leçon (définitive) : étoiles obligatoires, critères et commentaire facultatifs. */
export async function rateInstructor(
  lessonId: string,
  rating: { score: number; criteria?: Record<string, number>; comment?: string },
): Promise<void> {
  await http.post(`${BASE}/me/lessons/${lessonId}/instructor-rating`, rating);
}

/** « Ne pas noter » : la leçon n'est plus proposée. */
export async function dismissInstructorRating(lessonId: string): Promise<void> {
  await http.post(`${BASE}/me/lessons/${lessonId}/instructor-rating/dismiss`);
}

/** « Avez-vous déjà le code ? » (J46) : question posée tant que la fiche n'a pas de date du code. */
export interface CodeObtainedView {
  asked: boolean;
  codeObtainedAt: string | null;
}

export const CODE_OBTAINED_KEY = ['candidate', 'code-obtained'] as const;

export async function fetchCodeObtained(): Promise<CodeObtainedView> {
  const { data } = await http.get<CodeObtainedView>(`${BASE}/me/code-obtained`);
  return data;
}

/** « Oui » : le jour d'obtention (AAAA-MM-JJ), enregistré sur la fiche ; refusé si la fiche a déjà une date. */
export async function recordCodeObtained(obtainedOn: string): Promise<CodeObtainedView> {
  const { data } = await http.post<CodeObtainedView>(`${BASE}/me/code-obtained`, { obtainedOn });
  return data;
}
